import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, InputNumber } from "rsuite";
import "./MedicineModal.css";

const MedicineModal = ({
  isOpen,
  onClose,
  onSave,
  mode = "add",
  initialValues,
}) => {
  const [form, setForm] = useState({ name: "", price: "", qty: "", stock: 0 });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialValues) {
        setForm({
          name: initialValues.name ?? "",
          price: initialValues.price != null ? String(initialValues.price) : "",
          qty: initialValues.qty != null ? String(initialValues.qty) : "",
          stock: initialValues.stock != null ? Number(initialValues.stock) : 0,
        });
      } else {
        setForm({ name: "", price: "", qty: "", stock: 0 });
      }
      setErrors({});
    }
  }, [isOpen, initialValues]);

  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = "Medicine name is required";
    }

    const priceNum = Number(form.price);
    if (!form.price.trim()) {
      nextErrors.price = "Price is required";
    } else if (Number.isNaN(priceNum) || priceNum <= 0) {
      nextErrors.price = "Enter a valid price greater than 0";
    }

    // Quantity description (e.g., "1 TAB", "500 gm", "100 ml")
    if (!form.qty.trim()) {
      nextErrors.qty = "Quantity description is required";
    }

    // Stock is per-branch now; only validate on add.
    if (mode !== "edit" && form.stock < 0) {
      nextErrors.stock = "Stock count must be 0 or greater";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    setSubmitting(true);
    const payload = {
      name: form.name.trim(),
      price: Number(form.price),
      qty: form.qty.trim(),
    };
    // Per backend §10: stock is no longer a field on the medicine itself.
    // It's only used on add (as the initial value seeded across every branch).
    // On edit, omit it entirely — admins must use Stock Management.
    if (mode !== "edit") {
      payload.stock = Number(form.stock);
    }
    onSave(payload);
    setSubmitting(false);
    setForm({ name: "", price: "", qty: "", stock: 0 });
  };

  const handleClose = () => {
    if (submitting) return;
    setErrors({});
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} backdrop={submitting ? "static" : true} size="sm">
      <Modal.Header>
        <Modal.Title>{mode === "edit" ? "Edit medicine" : "Add medicine"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Medicine name</Form.ControlLabel>
            <Input
              name="name"
              placeholder="e.g. Ashwagandha Tablets"
              value={form.name}
              onChange={(value) => setForm((prev) => ({ ...prev, name: value }))}
              errorMessage={errors.name}
            />
          </Form.Group>

          <Form.Group style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <Form.ControlLabel>Price (Rs)</Form.ControlLabel>
              <InputNumber
                name="price"
                placeholder="0.00"
                min={0}
                step={0.01}
                value={form.price ? Number(form.price) : null}
                onChange={(value) => setForm((prev) => ({ ...prev, price: value ? String(value) : "" }))}
                style={{ width: "100%" }}
              />
              {errors.price && (
                <div style={{ color: "#dc2626", fontSize: "0.875rem", marginTop: "4px" }}>
                  {errors.price}
                </div>
              )}
            </div>

            {mode !== "edit" && (
              <div style={{ flex: 1 }}>
                <Form.ControlLabel>Initial stock (per branch)</Form.ControlLabel>
                <Input
                  name="stock"
                  placeholder="0"
                  value={form.stock === 0 ? "" : String(form.stock)}
                  onChange={(value) => {
                    const numValue = value === "" ? 0 : Number(value);
                    setForm((prev) => ({ ...prev, stock: numValue }));
                  }}
                  style={{ width: "100%" }}
                />
                {errors.stock && (
                  <div style={{ color: "#dc2626", fontSize: "0.875rem", marginTop: "4px" }}>
                    {errors.stock}
                  </div>
                )}
                <div className="med-field-hint" style={{ color: "#64748b", fontSize: "0.8125rem", marginTop: "4px" }}>
                  Seeded for every branch. Adjust per-branch in Stock Management.
                </div>
              </div>
            )}
          </Form.Group>

          {mode === "edit" && (
            <div
              style={{
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                padding: "10px 12px",
                color: "#475569",
                fontSize: "0.85rem",
                marginBottom: 12,
              }}
            >
              Stock is tracked per branch. Use <strong>Stock Management</strong> to adjust quantities for a specific branch.
            </div>
          )}

          <Form.Group>
            <Form.ControlLabel>Quantity Description</Form.ControlLabel>
            <Input
              name="qty"
              placeholder="e.g. 1 TAB, 500 gm, 100 ml, 5 ml syrup"
              value={form.qty}
              onChange={(value) => setForm((prev) => ({ ...prev, qty: value }))}
              style={{ width: "100%" }}
            />
            {errors.qty && (
              <div style={{ color: "#dc2626", fontSize: "0.875rem", marginTop: "4px" }}>
                {errors.qty}
              </div>
            )}
            <div className="med-field-hint" style={{ color: "#64748b", fontSize: "0.8125rem", marginTop: "4px" }}>
              Describe the quantity per unit (e.g., "1 tablet", "500 mg", "10 ml")
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          appearance="primary"
          style={{ backgroundColor: "#198754", color: "white" }}
          disabled={submitting}
        >
          {submitting
            ? mode === "edit"
              ? "Saving…"
              : "Adding…"
            : mode === "edit"
            ? "Save changes"
            : "Add medicine"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MedicineModal;


